import React, { useState, useEffect } from 'react';
import { playerUpdates } from '../api';
import ShareMatchForm from './Bookings/ShareMatchForm';
import PostThoughtForm from './Bookings/PostThoughtForm';
import UpdateCard from './Bookings/UpdateCard';
import ChatRoom from './Chat/ChatRoom';
import playtomicLogo from './Bookings/assets/playtomic-logo.png';
import './PlayerUpdatesTab.css';

/**
 * PlayerUpdatesTab - Player interface for sharing Playtomic matches and
 * posting reflections with their coach.
 *
 * Also the home of the team chat (the coach and all their players), which replaced asking the coach a question.
 */
const PlayerUpdatesTab = ({ studentId, studentName, currentCoachId }) => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all'); // 'all', 'match', 'thought'
  const [showMatchForm, setShowMatchForm] = useState(false);
  const [showThoughtForm, setShowThoughtForm] = useState(false);

  useEffect(() => {
    if (studentId) {
      loadUpdates();
    }
  }, [studentId, currentCoachId]);

  const loadUpdates = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await playerUpdates.getMyUpdates(currentCoachId ? { coachId: currentCoachId } : {});
      setUpdates(response.data.updates || []);
    } catch (err) {
      console.error('Error loading updates:', err);
      setError('Failed to load updates');
    } finally {
      setLoading(false);
    }
  };

  const handleMatchSubmit = async (playtomicUrl, matchThoughts) => {
    try {
      await playerUpdates.shareMatch({
        coachId: currentCoachId,
        playtomicUrl,
        matchThoughts
      });
      setShowMatchForm(false);
      await loadUpdates();
    } catch (err) {
      console.error('Error sharing match:', err);
      alert('Failed to share match: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleThoughtSubmit = async (thoughtText) => {
    try {
      await playerUpdates.postThought({
        coachId: currentCoachId,
        thoughtText
      });
      setShowThoughtForm(false);
      await loadUpdates();
    } catch (err) {
      console.error('Error posting thought:', err);
      alert('Failed to post thought');
    }
  };

  const handleDelete = async (updateId) => {
    if (!window.confirm('Delete this update? This action cannot be undone.')) {
      return;
    }

    try {
      await playerUpdates.deleteUpdate(updateId);
      setUpdates(updates.filter(u => u._id !== updateId));
    } catch (err) {
      console.error('Error deleting update:', err);
      alert('Failed to delete update');
    }
  };

  // Filter updates based on selected type
  const filteredUpdates = filterType === 'all'
    ? updates
    : updates.filter(u => u.type === filterType);

  if (!studentId) {
    return null;
  }

  return (
    <div className="player-updates-tab">
      <div className="updates-hero">
        <div className="updates-hero-text">
          <h3>Updates &amp; Insights</h3>
          <p className="updates-subtext">
            Share match results, post a reflection or chat with {studentName} and the other players
          </p>
        </div>
        <div className="updates-hero-stats">
          <div className="updates-stat">
            <strong>{updates.filter(u => u.type === 'match').length}</strong>
            matches
          </div>
          <div className="updates-stat">
            <strong>{updates.filter(u => u.type === 'thought').length}</strong>
            thoughts
          </div>
        </div>
      </div>

      {error && (
        <div className="updates-error">
          ❌ {error}
        </div>
      )}

      {/* Action Buttons */}
      <div className="updates-actions">
        <button
          className="action-btn match-btn"
          onClick={() => setShowMatchForm(!showMatchForm)}
        >
          <span className="action-icon"><img src={playtomicLogo} alt="" className="playtomic-icon" /></span>
          <span className="action-text">
            <span className="action-title">Share Match</span>
            <span className="action-caption">Send a Playtomic result</span>
          </span>
        </button>
        <button
          className="action-btn thought-btn"
          onClick={() => setShowThoughtForm(!showThoughtForm)}
        >
          <span className="action-icon">💭</span>
          <span className="action-text">
            <span className="action-title">Post Thought</span>
            <span className="action-caption">Reflect on your game</span>
          </span>
        </button>
      </div>

      {/* Forms */}
      {showMatchForm && (
        <ShareMatchForm
          onSubmit={handleMatchSubmit}
          onCancel={() => setShowMatchForm(false)}
        />
      )}
      {showThoughtForm && (
        <PostThoughtForm
          onSubmit={handleThoughtSubmit}
          onCancel={() => setShowThoughtForm(false)}
        />
      )}

      {/* Team chat: the coach and all their players */}
      <div className="updates-questions">
        <ChatRoom />
      </div>

      {!loading && updates.length > 0 && (
        <h4 className="updates-section-title">🗂️ Your Updates</h4>
      )}

      {/* Filter Buttons */}
      {!loading && updates.length > 0 && (
        <div className="filter-buttons">
          <button
            className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
            onClick={() => setFilterType('all')}
          >
            All ({updates.length})
          </button>
          <button
            className={`filter-btn ${filterType === 'match' ? 'active' : ''}`}
            onClick={() => setFilterType('match')}
          >
            Matches ({updates.filter(u => u.type === 'match').length})
          </button>
          <button
            className={`filter-btn ${filterType === 'thought' ? 'active' : ''}`}
            onClick={() => setFilterType('thought')}
          >
            Thoughts ({updates.filter(u => u.type === 'thought').length})
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="updates-loading">
          Loading updates...
        </div>
      )}

      {/* Empty State */}
      {!loading && updates.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">🎾</div>
          <p>No updates yet</p>
          <p>Share a match or post a thought to keep your coach in the loop</p>
        </div>
      )}

      {/* Updates Feed */}
      {!loading && filteredUpdates.length > 0 && (
        <div className="updates-list">
          {filteredUpdates.map(update => (
            <UpdateCard
              key={update._id}
              update={update}
              onDelete={() => handleDelete(update._id)}
              isOwnUpdate={true}
            />
          ))}
        </div>
      )}

      {/* No filtered results */}
      {!loading && updates.length > 0 && filteredUpdates.length === 0 && (
        <div className="empty-state">
          <p>No {filterType}s yet</p>
        </div>
      )}
    </div>
  );
};

export default PlayerUpdatesTab;
